import classnames from "classnames";
import {PropsWithChildren, ReactNode} from "react";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {EUDisclaimer} from "@/components/disclaimer/EUDisclaimer.tsx";
import {useFeatureBase} from "@/components/feedback/FeatureBase.ts";
import {UptimeStatus} from "@/components/feedback/UptimeStatus.tsx";
import {FreeTrialExpired} from "@/components/freeTrialExpired/FreeTrialExpired.tsx";
import {useGTM} from "@/components/trackers/GoogleTagEvents.ts";
import {UptimeToaster} from "@/components/uptimeToaster/UptimeToaster.tsx";

import Header from "./Header.tsx";

interface IContentLayoutProps extends PropsWithChildren {
  children?: ReactNode;
  title?: ReactNode;
  actionsBar?: ReactNode;
  banner?: ReactNode;
  extraHeaderContent?: ReactNode;
  subHeader?: ReactNode;
  contentClass?: string;
  showStatusBanner?: boolean;
  fullHeight?: boolean;
}

const ContentContainer = ({
  title,
  actionsBar,
  children,
  banner,
  extraHeaderContent,
  subHeader,
  contentClass,
  showStatusBanner = false,
  fullHeight = false,
}: IContentLayoutProps) => {
  const {brand, status, user} = useCurrentUserContext();
  useGTM(user);
  const {init: initFeatureBase} = useFeatureBase();
  if (user) {
    initFeatureBase(user);
  }

  return (
    <>
      <div
        className={classnames("content-container-header border-bottom", {
          "pb-0": extraHeaderContent,
          "p-4": title,
          "pb-3": title && !extraHeaderContent,
        })}
      >
        {banner}
        {showStatusBanner && status && (
          <UptimeStatus date={status.date} title={status.title} statusPageURL={brand.statusPageURL} />
        )}
        <UptimeToaster />
        <EUDisclaimer />
        {title && (
          <Header title={title} actionsBar={actionsBar}>
            {extraHeaderContent}
          </Header>
        )}
      </div>
      {subHeader && <div className="content-container-header">{subHeader}</div>}
      <div
        className={classnames(
          "content-container",
          fullHeight ? "content-container-full-height" : "p-4 pt-2",
          contentClass
        )}
      >
        {user!.account!.free_trial_expires_at && <FreeTrialExpired />}
        {children && (
          <div className={fullHeight ? "content-container-full-height-body" : "mt-md-3 mt-2 pb-1"}>{children}</div>
        )}
      </div>
    </>
  );
};

export default ContentContainer;
